import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormHelperText,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { LoadingButton } from "@mui/lab";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../layout/Layout";
import { apiData } from "../../services/api_service";
import { proformaQuoteGrnAPI, proformaQuoteGrnCandidateAPI } from "../../config";

// Section badge used to number the cards below, matching the Orders/Proforma forms.
const SectionBadge = ({ children }) => (
  <Box
    sx={{
      width: 26,
      height: 26,
      borderRadius: "7px",
      bgcolor: "#EEF2FF",
      color: "#2F5FED",
      fontSize: 12,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const cardSx = {
  border: "1px solid #E3E6EA",
  borderRadius: "14px",
  p: { xs: 2, sm: 3 },
  mb: 2.5,
  backgroundColor: "#fff",
  boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
};

// Per-row validation: returned qty must be within range, and a damage note is
// mandatory once the item is marked damaged.
const buildRowSchema = (row) =>
  Yup.object().shape({
    quantity: Yup.number()
      .typeError("Enter a valid quantity")
      .min(1, "Must be greater than 0")
      .max(
        Number(row.returnable_quantity) || 0,
        `Cannot exceed ${row.returnable_quantity}`
      )
      .required("Returned qty is required"),
    condition: Yup.string().required("Condition is required"),
    damaged_reason: Yup.string().when("condition", {
      is: "damaged",
      then: (schema) =>
        schema.trim().required("Damage note is required for damaged items"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

const validateRowSync = (row) => {
  try {
    buildRowSchema(row).validateSync(
      {
        quantity: row.quantity,
        condition: row.condition,
        damaged_reason: row.damaged_reason,
      },
      { abortEarly: false }
    );
    return null;
  } catch (err) {
    const fieldErrors = {};
    (err.inner || []).forEach((e) => {
      if (e.path && !fieldErrors[e.path]) fieldErrors[e.path] = e.message;
    });
    return fieldErrors;
  }
};

const GenerateGrn = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [proforma, setProforma] = useState(null);
  const [rows, setRows] = useState([]);
  const [grnDate, setGrnDate] = useState(dayjs());
  const [remarks, setRemarks] = useState("");
  const [rowErrors, setRowErrors] = useState({}); // keyed by product_id
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    const fetchCandidate = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await apiData(
          "get",
          `${proformaQuoteGrnCandidateAPI}/${id}/grn-candidate`
        );
        if (res.success) {
          setProforma(res.data?.proforma || null);
          setRows(
            (res.data?.items || []).map((item) => ({
              ...item,
              selected: Number(item.returnable_quantity || 0) > 0,
              quantity: Number(item.returnable_quantity || 0),
              condition: "good",
              damaged_reason: "",
            }))
          );
        } else {
          setError(res.message || res.msg || "Failed to load Proforma details.");
        }
      } catch (err) {
        console.error("Failed to load GRN details:", err);
        setError("Failed to load Proforma details.");
      } finally {
        setLoading(false);
      }
    };
    fetchCandidate();
  }, [id]);

  const selectedRows = useMemo(
    () => rows.filter((row) => row.selected && Number(row.quantity) > 0),
    [rows]
  );

  const totalReturnQty = selectedRows.reduce(
    (sum, row) => sum + Number(row.quantity || 0),
    0
  );

  // Re-validates a single row once the user has already attempted a submit,
  // so the red outline clears/appears live as they fix things.
  const revalidateRow = (row) => {
    setRowErrors((prev) => {
      const updated = { ...prev };
      if (!row.selected || Number(row.quantity) <= 0) {
        delete updated[row.product_id];
        return updated;
      }
      const fieldErrors = validateRowSync(row);
      if (fieldErrors) {
        updated[row.product_id] = fieldErrors;
      } else {
        delete updated[row.product_id];
      }
      return updated;
    });
  };

  const updateRow = (index, patch) => {
    const updatedRow = { ...rows[index], ...patch };
    setRows((prev) =>
      prev.map((row, rowIndex) => (rowIndex === index ? updatedRow : row))
    );
    if (attempted) {
      revalidateRow(updatedRow);
    }
  };

  const validate = () => {
    if (selectedRows.length === 0) {
      setError("Select at least one item with returned quantity.");
      setRowErrors({});
      return false;
    }

    const nextErrors = {};
    let valid = true;
    selectedRows.forEach((row) => {
      const fieldErrors = validateRowSync(row);
      if (fieldErrors) {
        valid = false;
        nextErrors[row.product_id] = fieldErrors;
      }
    });
    setRowErrors(nextErrors);

    if (!valid) {
      setError("Please fix the highlighted fields below.");
    }
    return valid;
  };

  const handleSubmit = async () => {
    setError("");
    setSuccess("");
    setAttempted(true);
    if (!validate()) return;

    try {
      setSubmitting(true);
      const res = await apiData("post", `${proformaQuoteGrnAPI}/${id}/grn`, {
        grn_date: grnDate ? dayjs(grnDate).format("YYYY-MM-DD") : null,
        remarks,
        items: selectedRows.map((row) => ({
          product_id: row.product_id,
          quantity: Number(row.quantity),
          condition: row.condition,
          damaged_reason:
            row.condition === "damaged" ? row.damaged_reason : null,
        })),
      });
      if (res.success) {
        setSuccess(res.message || "GRN generated successfully.");
        if (res.data?.pdf_url) {
          window.open(res.data.pdf_url, "_blank");
        }
        setTimeout(() => navigate("/proforma-quote/list"), 900);
      } else {
        setError(res.message || res.msg || "Failed to generate GRN.");
      }
    } catch (err) {
      console.error("Generate GRN failed:", err);
      setError("Failed to generate GRN.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography className="page_title" textAlign="left">
              Generate GRN (Goods Received Note)
            </Typography>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Link underline="hover" color="inherit" href="/proforma-quote/list">
                Proforma
              </Link>
              <Typography color="text.primary">Generate GRN</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} md={4} textAlign="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </Button>
          </Grid>
        </Grid>

        {loading ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mt: 3 }}>
                {success}
              </Alert>
            )}

            {/* Section 1: GRN details */}
            <Box sx={{ ...cardSx, mt: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <SectionBadge>1</SectionBadge>
                <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                  GRN Details
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Proforma No"
                    value={proforma?.proforma_number || ""}
                    fullWidth
                    size="small"
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Salesman"
                    value={proforma?.salesman_name || "-"}
                    fullWidth
                    size="small"
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Source Store"
                    value={proforma?.store_name || "-"}
                    fullWidth
                    size="small"
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <MobileDatePicker
                      label="GRN Date"
                      value={grnDate}
                      inputFormat="DD-MM-YYYY"
                      onChange={(newValue) => setGrnDate(newValue)}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth size="small" />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Remarks"
                    value={remarks}
                    onChange={(event) => setRemarks(event.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Any notes about this return…"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Section 2: Items to return */}
            <Box sx={cardSx}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.25,
                  mb: 2.5,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <SectionBadge>2</SectionBadge>
                  <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                    Items to Return
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Mark an item "Damaged" to require a damage note.
                </Typography>
              </Box>

              <Box
                sx={{
                  overflowX: "auto",
                  border: "1px solid #E3E6EA",
                  borderRadius: "10px",
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          backgroundColor: "#F8FAFC",
                          color: "#475467",
                          fontWeight: 700,
                          fontSize: 12.5,
                          textTransform: "uppercase",
                          letterSpacing: "0.02em",
                          borderBottom: "1px solid #E3E6EA",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      <TableCell padding="checkbox" />
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Issued</TableCell>
                      <TableCell align="right">Invoiced</TableCell>
                      <TableCell align="right">Returned</TableCell>
                      <TableCell align="right">Returnable</TableCell>
                      <TableCell sx={{ minWidth: 110 }}>Return Qty</TableCell>
                      <TableCell sx={{ minWidth: 190 }}>Condition</TableCell>
                      <TableCell sx={{ minWidth: 240 }}>Damage Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            No items found for this Proforma.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row, index) => {
                        const disabled = Number(row.returnable_quantity || 0) <= 0;
                        const rowError = rowErrors[row.product_id];
                        const hasError = Boolean(rowError);
                        const isDamaged = row.condition === "damaged";
                        const errorMessage =
                          rowError?.quantity ||
                          rowError?.condition ||
                          rowError?.damaged_reason;
                        return (
                          <Fragment key={row.product_id}>
                            <TableRow
                              sx={{
                                backgroundColor: hasError
                                  ? "#FFFBFA"
                                  : row.selected
                                  ? "#F7FAFF"
                                  : "inherit",
                                borderLeft: hasError
                                  ? "3px solid #D92D20"
                                  : "3px solid transparent",
                                "& > td": {
                                  color: disabled ? "#98A2B3" : "inherit",
                                  borderBottom: errorMessage
                                    ? "none"
                                    : undefined,
                                },
                                "&:hover": {
                                  backgroundColor: hasError
                                    ? "#FFF1F0"
                                    : "#F5F8FC",
                                },
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={row.selected}
                                  disabled={disabled}
                                  onChange={(event) =>
                                    updateRow(index, {
                                      selected: event.target.checked,
                                      quantity: event.target.checked
                                        ? row.quantity || row.returnable_quantity
                                        : 0,
                                    })
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>
                                  {row.product_name || "-"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  SKU: {row.sku || "-"}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">{row.issued_quantity || 0}</TableCell>
                              <TableCell align="right">{row.converted_quantity || 0}</TableCell>
                              <TableCell align="right">
                                {row.already_returned_quantity || 0}
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={row.returnable_quantity || 0}
                                  size="small"
                                  sx={{
                                    bgcolor: disabled ? "#F2F4F7" : "#EEF4FF",
                                    color: disabled ? "#98A2B3" : "#0c54a0",
                                    fontWeight: 700,
                                    minWidth: 36,
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 110 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  disabled={!row.selected || disabled}
                                  value={row.quantity || ""}
                                  onFocus={(e) => e.target.select()}
                                  inputProps={{
                                    min: 0,
                                    max: row.returnable_quantity,
                                    style: { textAlign: "right" },
                                  }}
                                  error={Boolean(rowError?.quantity)}
                                  onChange={(event) =>
                                    updateRow(index, {
                                      quantity: Math.max(
                                        0,
                                        Math.min(
                                          Number(row.returnable_quantity || 0),
                                          Number(event.target.value || 0)
                                        )
                                      ),
                                    })
                                  }
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 190 }}>
                                <ToggleButtonGroup
                                  size="small"
                                  exclusive
                                  fullWidth
                                  disabled={!row.selected || disabled}
                                  value={row.condition}
                                  onChange={(event, value) => {
                                    if (!value) return;
                                    updateRow(index, {
                                      condition: value,
                                      // Clear a stale note if switching back to "good".
                                      damaged_reason:
                                        value === "damaged" ? row.damaged_reason : "",
                                    });
                                  }}
                                  sx={{
                                    "& .MuiToggleButton-root": {
                                      textTransform: "none",
                                      fontSize: 12.5,
                                      fontWeight: 600,
                                      py: 0.4,
                                      gap: 0.5,
                                      color: "#475467",
                                      borderColor: "#E3E6EA",
                                    },
                                    "& .Mui-selected.MuiToggleButton-root[value='good']": {
                                      bgcolor: "#ECFDF3 !important",
                                      color: "#067647 !important",
                                      borderColor: "#ABEFC6 !important",
                                    },
                                    "& .Mui-selected.MuiToggleButton-root[value='damaged']": {
                                      bgcolor: "#FEF3F2 !important",
                                      color: "#B42318 !important",
                                      borderColor: "#FECDCA !important",
                                    },
                                  }}
                                >
                                  <ToggleButton value="good">
                                    <CheckCircleRoundedIcon sx={{ fontSize: 15 }} />
                                    Good
                                  </ToggleButton>
                                  <ToggleButton value="damaged">
                                    <ReportProblemRoundedIcon sx={{ fontSize: 15 }} />
                                    Damaged
                                  </ToggleButton>
                                </ToggleButtonGroup>
                              </TableCell>
                              <TableCell sx={{ minWidth: 240 }}>
                                {isDamaged ? (
                                  <TextField
                                    size="small"
                                    fullWidth
                                    disabled={!row.selected || disabled}
                                    placeholder="Describe the damage…"
                                    value={row.damaged_reason}
                                    error={Boolean(rowError?.damaged_reason)}
                                    onChange={(event) =>
                                      updateRow(index, {
                                        damaged_reason: event.target.value,
                                      })
                                    }
                                  />
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.disabled"
                                    sx={{ fontStyle: "italic" }}
                                  >
                                    Not applicable
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                            {errorMessage && (
                              <TableRow
                                sx={{
                                  backgroundColor: "#FFFBFA",
                                  borderLeft: "3px solid #D92D20",
                                }}
                              >
                                <TableCell colSpan={9} sx={{ py: 0.5, border: 0 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.6,
                                      color: "#B42318",
                                    }}
                                  >
                                    <ErrorOutlineRoundedIcon sx={{ fontSize: 15 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                      {errorMessage}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Box>
              {attempted && Object.keys(rowErrors).length > 0 && (
                <FormHelperText error sx={{ mt: 1, ml: 0.5 }}>
                  Some rows above need attention before you can submit.
                </FormHelperText>
              )}
            </Box>

            {/* Section 3: Summary & submit */}
            <Box
              sx={{
                ...cardSx,
                mb: 0,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Returned Qty
                </Typography>
                <Typography fontWeight={700} fontSize={20}>
                  {totalReturnQty}
                </Typography>
              </Box>
              <LoadingButton
                loading={submitting}
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
              >
                Submit GRN
              </LoadingButton>
            </Box>
          </>
        )}
      </Grid>
    </Layout>
  );
};

export default GenerateGrn;
